import React, { useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { LED_COLORS, LED_MODES, SIM_LABELS } from '../data/simulatorContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const bandSrc = (key) => `${BACKEND_URL}/api/uploads/sim-band-${key}.png`;
const CROWD_COUNT = 50;

const SIM_CSS = `
@keyframes ledSlow { 0%,45% { opacity: 1 } 55%,100% { opacity: 0 } }
@keyframes ledFast { 0%,40% { opacity: 1 } 50%,100% { opacity: 0 } }
@keyframes ledFade { 0%,100% { opacity: 0.08 } 50% { opacity: 1 } }
.led-on { opacity: 1; }
.sim-slow .led-on { animation: ledSlow 1.3s steps(1,end) infinite; }
.sim-fast .led-on { animation: ledFast 0.36s steps(1,end) infinite; }
.sim-fade .led-on { animation: ledFade 2.4s ease-in-out infinite; }
.sim-wave .led-on { animation: ledFade 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .led-on { animation: none !important; opacity: 1 !important; } }
`;

const Bracelet = ({ colorKey, hex, alt, delay, glow }) => (
  <div className="relative">
    <img loading="lazy"
      src={bandSrc(colorKey)}
      alt=""
      aria-hidden="true"
      className="w-full h-auto select-none pointer-events-none"
      style={{ filter: 'brightness(0.16) saturate(0.35)' }}
      draggable="false"
    />
    <img
      src={bandSrc(colorKey)}
      alt={alt}
      loading="lazy"
      className="led-on absolute inset-0 w-full h-auto select-none pointer-events-none"
      style={{ animationDelay: `${delay}s`, filter: `drop-shadow(0 0 ${glow}px ${hex}66)` }}
      draggable="false"
    />
  </div>
);

const ColorSimulator = ({ allowCrowd = false, defaultCrowd = false }) => {
  const { lang } = useLang();
  const L = SIM_LABELS[lang] || SIM_LABELS.fr;
  const [color, setColor] = useState(LED_COLORS[0]);
  const [mode, setMode] = useState(LED_MODES[1]);
  const [crowd, setCrowd] = useState(allowCrowd && defaultCrowd);
  const colorName = color.name[lang] || color.name.fr;
  const alt = `Bracelet LED Party LED ${colorName}`;
  const count = crowd ? CROWD_COUNT : 5;
  const waveDelay = (i) => (mode.key === 'wave' ? (crowd ? (i % 12) * 0.09 : i * 0.22) : 0);

  return (
    <section className="mt-8" data-testid="color-simulator">
      <style>{SIM_CSS}</style>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Georgia, serif' }}>
        {L.title}
      </h2>
      <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto text-center mb-8">{L.subtitle}</p>

      <div className="rounded-2xl border border-gray-800 bg-gray-950 overflow-hidden">
        {/* Scène */}
        <div
          className={`relative sim-${mode.key} px-3 py-10 sm:px-6 sm:py-14 transition-colors duration-500`}
          style={{ background: `radial-gradient(circle at 50% 45%, ${color.hex}${crowd ? '2b' : '1f'}, #04040a 72%)` }}
          data-testid="simulator-stage"
        >
          {crowd ? (
            <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 sm:gap-2.5" data-testid="simulator-crowd">
              {Array.from({ length: CROWD_COUNT }, (_, i) => (
                <div key={i} style={{ transform: `translateY(${(i % 3) * 7}px) scale(${0.86 + ((i * 7) % 5) * 0.05})` }}>
                  <Bracelet colorKey={color.key} hex={color.hex} alt={alt} delay={waveDelay(i)} glow={10} />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 sm:gap-5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-1/5 max-w-[210px]"
                  style={{ transform: `translateY(${i % 2 === 0 ? 0 : 18}px) scale(${i === 2 ? 1.1 : 0.94})` }}
                >
                  <Bracelet colorKey={color.key} hex={color.hex} alt={alt} delay={waveDelay(i)} glow={22} />
                </div>
              ))}
            </div>
          )}
          <p className="text-center text-xs uppercase tracking-[0.2em] text-gray-500 mt-8" data-testid="simulator-selection">
            {L.selected} : <span className="text-white">{colorName}</span> · <span className="text-white">{mode.name[lang] || mode.name.fr}</span> · <span className="text-white">{count}</span>
          </p>
        </div>

        {/* Contrôles */}
        <div className="p-5 sm:p-6 border-t border-gray-800 bg-gray-900/60">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.color}</p>
          <div className="flex flex-wrap gap-2.5 mb-6" data-testid="simulator-colors">
            {LED_COLORS.map((c) => (
              <button
                key={c.key}
                onClick={() => setColor(c)}
                aria-label={c.name[lang] || c.name.fr}
                title={c.name[lang] || c.name.fr}
                className={`w-9 h-9 rounded-full transition-transform duration-200 hover:scale-110 ${color.key === c.key ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-110' : 'ring-1 ring-white/20'}`}
                style={{ backgroundColor: c.hex, boxShadow: color.key === c.key ? `0 0 18px ${c.hex}` : 'none' }}
                data-testid={`simulator-color-${c.key}`}
              />
            ))}
          </div>

          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.mode}</p>
          <div className={`flex flex-wrap gap-2 ${allowCrowd ? 'mb-6' : ''}`} data-testid="simulator-modes">
            {LED_MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m)}
                className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 border ${mode.key === m.key ? 'bg-white text-black border-white' : 'text-gray-300 border-gray-700 hover:border-gray-500'}`}
                data-testid={`simulator-mode-${m.key}`}
              >
                {m.name[lang] || m.name.fr}
              </button>
            ))}
          </div>

          {allowCrowd && (
            <>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.view}</p>
              <div className="flex flex-wrap gap-2" data-testid="simulator-views">
                {[{ k: false, label: L.viewFew }, { k: true, label: L.viewCrowd }].map(({ k, label }) => (
                  <button
                    key={String(k)}
                    onClick={() => setCrowd(k)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 border ${crowd === k ? 'bg-purple-600 text-white border-purple-600' : 'text-gray-300 border-gray-700 hover:border-gray-500'}`}
                    data-testid={`simulator-view-${k ? 'crowd' : 'few'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}

          <p className="text-gray-500 text-xs mt-5">{L.note}</p>
        </div>
      </div>
    </section>
  );
};

export default ColorSimulator;
